import { getRestartedResultId } from '@orientaction/utils';
import { round } from 'lodash';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { apiServiceNative } from '../../../../utils/fetchNative';
import RedesignImage from '../../shared/components/RedesignImage';
import locale from '../locale.json';
import { useResourceCardStyles } from './ResourceCard.styles';

export type SeriousGameCardAction = 'viewResult' | 'retake' | 'resume' | 'start';

type ResourceCardProps = {
  title: string;
  coverSrc: string;
  coverAlt?: string;
  isEbook?: boolean;
  isRestart?: boolean;
  assignationDate?: string;
  readingDate?: string | null;
  progressLabel?: string;
  progressValue?: number;
  isCompleted?: boolean;
  isNotStarted?: boolean;
  gameAction?: SeriousGameCardAction;
  actionLabel: string;
  onAction?: () => void;
  gameId?: number;
  idResult?: string | number | null;
  idLastPage?: number | null;
  linkTo?: boolean;
};

const ResourceCard = ({
  title,
  coverSrc,
  coverAlt = locale.coverAlt,
  isEbook = false,
  isRestart = false,
  assignationDate = '',
  readingDate = null,
  progressLabel = '',
  progressValue = 0,
  isCompleted = false,
  isNotStarted = false,
  gameAction,
  actionLabel,
  onAction,
  gameId,
  idResult,
  idLastPage,
  linkTo = false,
}: ResourceCardProps) => {
  const classes = useResourceCardStyles();
  const history = useHistory();
  const [restarting, setRestarting] = useState(false);
  const resolvedGameAction = gameAction || (actionLabel === locale.start ? 'start' : undefined);
  const isStartAction = resolvedGameAction === 'start' || actionLabel === locale.start;
  const showSynthesisActions = Boolean(
    resolvedGameAction === 'viewResult' && idResult && gameId && !linkTo
  );

  const relancerGame = async () => {
    if (!idResult || !gameId || restarting) {
      return;
    }

    setRestarting(true);

    try {
      const response = await apiServiceNative.put(`/api/restart-game-result/${idResult}`, {});
      const newIdResult = getRestartedResultId(response);
      if (!newIdResult) {
        return;
      }
      history.push(`/game/view/${gameId}/${newIdResult}`);
    } finally {
      setRestarting(false);
    }
  };

  const openSynthesis = () => {
    if (!idResult) {
      return;
    }
    history.push(`/game/result/${idResult}`);
  };

  const handleClick = () => {
    if (linkTo && onAction) {
      onAction();
      return;
    }

    if (!linkTo && gameId) {
      if (resolvedGameAction === 'viewResult') {
        openSynthesis();
        return;
      }

      if (resolvedGameAction === 'retake') {
        relancerGame();
        return;
      }

      if (resolvedGameAction === 'resume') {
        history.push(`/game/play/${idResult}/${idLastPage}`);
        return;
      }

      history.push(`/game/view/${gameId}`);
    }
  };

  const showProgress = Boolean(progressLabel);
  const trackClass = isNotStarted
    ? classes.progressTrackNotStarted
    : isCompleted
    ? classes.progressTrackMuted
    : classes.progressTrack;
  const fillClass = isCompleted ? classes.progressFillSuccess : classes.progressFill;
  const fillWidth = `${Math.min(Math.max(progressValue, 0), 100)}%`;

  return (
    <article className={classes.card}>
      <div className={`${classes.cardCover} ${isRestart ? classes.cardCoverDimmed : ''}`}>
        <RedesignImage src={coverSrc} alt={coverAlt} />
      </div>

      <div className={classes.cardBody}>
        <div className={classes.cardTitleBlock}>
          <h3 className={classes.cardTitle}>{title}</h3>
          <div className={classes.cardTitleSpacer} aria-hidden="true" />
          {showProgress ? (
            <div className={classes.progressRow}>
              <div className={trackClass}>
                {!isNotStarted && (
                  <div className={fillClass} style={{ width: fillWidth }} />
                )}
              </div>
              {progressLabel && <span className={classes.progressLabel}>{progressLabel}</span>}
            </div>
          ) : (
            <div className={classes.progressRowPlaceholder} aria-hidden="true" />
          )}
        </div>

        {showSynthesisActions ? (
          <div className={classes.cardActions}>
            <button type="button" className={classes.cardActionButton} onClick={openSynthesis}>
              {locale.viewResult}
            </button>
          </div>
        ) : (
          <button
            type="button"
            className={isStartAction ? classes.cardButtonPrimary : classes.cardButton}
            onClick={handleClick}>
            {actionLabel}
          </button>
        )}
      </div>

      {isEbook && (assignationDate || readingDate) && (
        <div className={classes.cardMeta}>
          {assignationDate && (
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>{locale.assignedAt}</span>
              <span className={classes.metaValue}>{assignationDate}</span>
            </div>
          )}
          {readingDate && (
            <div className={classes.metaRow}>
              <span className={classes.metaLabel}>{locale.consultedAt}</span>
              <span className={classes.metaValue}>{readingDate}</span>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const hasGameResultId = (book: any) => book?.idResult != null && book.idResult !== '';

export const getSeriousGameCardState = (book: any) => {
  const action: SeriousGameCardAction =
    book?.isCompletedSynthesis && hasGameResultId(book)
      ? 'viewResult'
      : book?.resultStatus === 'completed' && hasGameResultId(book)
      ? 'retake'
      : book?.resultStatus === 'in_progress' && hasGameResultId(book)
      ? 'resume'
      : 'start';

  const progressValue = Math.min(Math.max(Number(book?.progress) || 0, 0), 100);
  const isNotStarted = progressValue === 0;
  const isCompleted = progressValue === 100;

  return {
    action,
    actionLabel:
      action === 'viewResult'
        ? locale.viewResult
        : action === 'retake'
        ? locale.retakeSeriousGame
        : action === 'resume'
        ? locale.resumeReading
        : locale.start,
    label: isCompleted
      ? locale.finished
      : isNotStarted
      ? locale.notStarted
      : `${round(progressValue)}%`,
    value: progressValue,
    isNotStarted,
    isCompleted,
  };
};

export const getGameProgress = (book: any) => {
  const { label, value, isNotStarted, isCompleted } = getSeriousGameCardState(book);
  return { label, value, isNotStarted, isCompleted };
};

export const getEbookProgress = (hasConsultation: boolean) => {
  if (hasConsultation) {
    return { label: locale.finished, value: 100, isNotStarted: false, isCompleted: true };
  }

  return { label: locale.notStarted, value: 0, isNotStarted: true, isCompleted: false };
};

export default ResourceCard;
